import { useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorInput,
  EntitySelectorPool,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type TableFilter,
} from "@salestrak/ui";
import { usePoolSelection } from "#app/components/entity-selectors/use-pool-selection";

/**
 * The entity-filter body for `DataTableFilterButton`'s `renderEntityFilter`
 * slot: an inline entity selector over the filter def's pools, round-tripping
 * the filter's id-array values through `EntitySelection`s.
 *
 * The library particle renders NOTHING for an entity filter it was not handed
 * a renderer for — entity rows, their avatars and their vocabulary are the
 * app's, not the library's. That silence is why every `DataTableFilterButton`
 * call site passes `renderTableEntityFilter`: a table whose filter set is
 * entity-only (teams) otherwise opens an empty "Filter by" menu.
 */
const NO_POOLS: EntityPoolConfig[] = [];

export function TableEntityFilter({
  filter,
  setValues,
}: {
  filter: TableFilter<EntityItemConfig>;
  setValues: (key: string, values: string[]) => void;
}) {
  const selectorConfig = filter.entitySelectorConfig;

  const { selections, toIds } = usePoolSelection(
    selectorConfig?.pools ?? NO_POOLS,
    filter.values,
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => setValues(filter.key, toIds(next)),
    [filter.key, toIds, setValues],
  );

  if (!selectorConfig) return null;

  return (
    <EntitySelector
      variant="inline"
      config={selectorConfig}
      value={selections}
      onValueChange={handleValueChange}
    >
      <EntitySelectorInput />
      {selectorConfig.pools.map((pool) => (
        <EntitySelectorPool key={pool.id} value={pool.id} />
      ))}
    </EntitySelector>
  );
}

/** Stable render-prop form for `DataTableFilterButton renderEntityFilter=`. */
export function renderTableEntityFilter(args: {
  filter: TableFilter<EntityItemConfig>;
  setValues: (key: string, values: string[]) => void;
}) {
  return <TableEntityFilter {...args} />;
}
